<template>
  <app-collapse
    heading="Pricing"
    :is-open="true"
  >
    <app-card
      variant="muted"
      body-class="space-y-6"
    >
      <data-property
        label="Room type"
        direction="horizontal"
        :value="reservation?.data?.room_type?.code || 'N/A'"
        :is-loading="isLoading"
      />

      <data-property
        label="Currency"
        direction="horizontal"
        :is-loading="isLoading"
      >
        <template #value>
          <country-flag
            :country-code="CurrencyCountryCodes[currencyCode] || 'XX'"
            class="w-4 h-4"
          />
          <span>{{ currencyCode }}</span>
        </template>
      </data-property>

      <div class="flex flex-col gap-2">
        <reservation-details-pricing-breakdown
          label="Total fare"
          :amount="totalFare"
        >
          <data-property
            label="Base fare"
            direction="horizontal"
            has-leader
            :is-loading="isLoading"
            :value="baseFare"
          />
          <data-property
            label="Taxes"
            direction="horizontal"
            has-leader
            :is-loading="isLoading"
            :value="taxes"
          />
          <data-property
            label="Fees"
            direction="horizontal"
            has-leader
            :is-loading="isLoading"
            :value="fees"
          />
        </reservation-details-pricing-breakdown>
        <reservation-details-pricing-breakdown
          label="Commission"
          :amount="commission"
        >
          <data-property
            label="Commissionable fare"
            direction="horizontal"
            has-leader
            :is-loading="isLoading"
            :value="commissionableFare"
          />
          <template v-if="hasCommissionRates">
            <div class="flex flex-col gap-2">
              <reservation-details-commission-rate
                v-for="commRate in reservation?.data?.commission_rates"
                :key="commRate.id"
                :commission-rate="commRate"
                :currency-code="currencyCode || 'USD'"
              />
            </div>
          </template>
          <data-property
            label="Effective commission rate"
            direction="horizontal"
            has-leader
            :is-loading="isLoading"
            :value="effectiveCommissionRate"
          />
        </reservation-details-pricing-breakdown>
        <reservation-details-pricing-breakdown
          label="VAT"
          :amount="vat"
          disabled
        />
      </div>

      <div class="divider h-0 mb-2"></div>

      <data-property
        is-bold
        size="lg"
        label="Total due"
        direction="horizontal"
        :is-loading="isLoading"
        :value="totalPayable"
      />

      <data-property
        v-if="receivedCurrencyCode"
        is-bold
        size="lg"
        label="Total received"
        direction="horizontal"
        :is-loading="isLoading"
        :value="receivedTotalPayable"
      />
    </app-card>
  </app-collapse>
</template>

<script setup>
import { computed } from 'vue';
import { formatNumber } from '@/utils/number';
import { CurrencyCountryCodes } from '@/constants/currencies';
import CountryFlag from '@/components/ui/CountryFlag.vue';
import ReservationDetailsCommissionRate from './ReservationDetailsCommissionRate.vue';
import ReservationDetailsPricingBreakdown from './ReservationDetailsPricingBreakdown.vue';

const props = defineProps({
  reservation: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const currencyCode = computed(() => props.reservation?.data?.currency_alpha_code);
const hasCommissionRates = computed(() => props.reservation?.data?.commission_rates?.length > 0);
const effectiveCommissionRate = computed(() => {
  const rate = props.reservation?.data?.effective_commission_rate;
  return rate != null ? `${rate}%` : 'N/A';
});
const receivedCurrencyCode = computed(() => props.reservation?.data?.received_currency_alpha_code);

const baseFare = computed(() => money(props.reservation?.data?.base_fare));
const commission = computed(() => money(props.reservation?.data?.commission));
const commissionableFare = computed(() => money(props.reservation?.data?.commissionable_fare));
const fees = computed(() => money(props.reservation?.data?.fees));
const receivedTotalPayable = computed(() =>
  formatNumber({
    value: props.reservation?.data?.received_total_payable || props.reservation?.data?.received_commission,
    options: {
      style: 'currency',
      currency: receivedCurrencyCode.value,
    },
  })
);
const taxes = computed(() => money(props.reservation?.data?.taxes));
const totalFare = computed(() => money(props.reservation?.data?.total_fare));
const totalPayable = computed(() => money(props.reservation?.data?.total_payable));
const vat = computed(() => money(props.reservation?.data?.vat));

const money = (value) => {
  return formatNumber({
    value,
    options: {
      style: 'currency',
      currency: currencyCode.value || 'USD',
    },
  });
};
</script>
